import { useQueryClient } from '@tanstack/react-query'
import { useRouterState } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { getSubscriptions, type BillingState } from '@/requests/billing'
import { useShop } from '@/context/shop-context'
import { setCachedPlan } from '@/lib/plan-cache'

const POLL_MS = 2000
const MAX_ATTEMPTS = 15

function resolveShopId(shop: { _id?: string; id?: string } | null): string {
  if (!shop) return ''
  return shop._id ?? (shop as { id?: string }).id ?? ''
}

function isPlanEntitlement(key: string): boolean {
  return (
    !key.startsWith('mp_plugin_') &&
    (key === 'plan_plus' || key === 'plan_pro' || key === 'plan_enterprise')
  )
}

function isActivated(
  data: BillingState,
  mode: 'plan' | 'plugin',
  pluginSlug: string,
): boolean {
  const activeSubs = data.subscriptions.filter(
    (s) => s.status === 'ACTIVE' || s.status === 'PAST_DUE',
  )

  if (mode === 'plugin' && pluginSlug) {
    const slug = pluginSlug.toLowerCase()
    if (data.entitlements.plugins.some((p) => p.toLowerCase() === slug)) {
      return true
    }
    const key = `mp_plugin_${slug}`
    return activeSubs.some((s) => s.entitlement === key)
  }

  if (data.entitlements.plan !== 'FREE') {
    return true
  }
  return activeSubs.some((s) => isPlanEntitlement(s.entitlement))
}

function parseCheckoutPending(search: string): boolean {
  const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  return q.get('checkout') === 'success' && q.get('subscription') === 'pending'
}

/**
 * PRD FE-6 / G-20: after Lemon Squeezy redirect with `?checkout=success&subscription=pending`,
 * poll subscriptions until activation or timeout.
 */
export function usePostCheckoutPolling(): {
  isPolling: boolean
  pending: boolean
  activated: boolean
} {
  const queryClient = useQueryClient()
  const { activeShop } = useShop()
  const shopId = resolveShopId(activeShop)
  const searchStr = useRouterState({
    select: (s) => (typeof s.location.search === 'string' ? s.location.search : ''),
  })

  const [isPolling, setIsPolling] = useState(false)
  const [pending, setPending] = useState(false)
  const [activated, setActivated] = useState(false)

  const checkoutPluginSlug = useMemo(() => {
    const q = new URLSearchParams(
      searchStr.startsWith('?') ? searchStr.slice(1) : searchStr,
    )
    const fromRouter = q.get('plugin_slug') ?? ''
    if (fromRouter) return fromRouter
    if (typeof window === 'undefined') return ''
    const wq = new URLSearchParams(window.location.search)
    return wq.get('plugin_slug') ?? ''
  }, [searchStr])

  const activationMode: 'plan' | 'plugin' = checkoutPluginSlug ? 'plugin' : 'plan'

  const shouldPoll = useMemo(() => {
    const fromRouter = parseCheckoutPending(searchStr)
    const fromWindow =
      typeof window !== 'undefined' &&
      parseCheckoutPending(window.location.search)
    return fromRouter || fromWindow
  }, [searchStr])

  useEffect(() => {
    if (!shouldPoll || !shopId) {
      setActivated(false)
      setPending(false)
      setIsPolling(false)
      return
    }

    let cancelled = false
    let finished = false
    let attempts = 0
    let intervalId: ReturnType<typeof setInterval> | null = null

    const finish = (kind: 'activated' | 'pending') => {
      if (cancelled || finished) return
      finished = true
      setIsPolling(false)
      if (kind === 'activated') {
        setActivated(true)
        setPending(false)
        void queryClient.invalidateQueries({ queryKey: ['billing', 'subscriptions'] })
      } else {
        setPending(true)
        setActivated(false)
      }
      if (intervalId) {
        clearInterval(intervalId)
        intervalId = null
      }
    }

    const poll = async () => {
      if (cancelled || finished) return
      attempts += 1
      try {
        const data = await getSubscriptions(shopId)
        if (cancelled || finished) return
        if (isActivated(data, activationMode, checkoutPluginSlug)) {
          setCachedPlan(shopId, data.entitlements.plan)
          finish('activated')
          return
        }
        if (attempts >= MAX_ATTEMPTS) {
          finish('pending')
        }
      } catch {
        if (cancelled || finished) return
        if (attempts >= MAX_ATTEMPTS) {
          finish('pending')
        }
      }
    }

    setIsPolling(true)
    void poll()
    intervalId = setInterval(() => {
      void poll()
    }, POLL_MS)

    return () => {
      cancelled = true
      if (intervalId) {
        clearInterval(intervalId)
      }
      setIsPolling(false)
    }
  }, [shouldPoll, shopId, queryClient, activationMode, checkoutPluginSlug])

  return { isPolling, pending, activated }
}
